import { MapPin, Plus, Trash2, Edit2, Check, Loader2, Building2, ChevronRight, ChevronLeft, Dumbbell, Sparkles } from 'lucide-react';
import React, { useState, useEffect, useCallback } from 'react';

import { supabase } from '../../../lib/supabase';
import { CompanyData } from '../../../store/onboarding.store';
import { AddressData } from '../../../lib/utils/geocoding';
import { LocationDetailsStep } from './LocationDetailsStep';
import SportsStep from './SportsStep';
import AmenitiesStep from './AmenitiesStep';

interface Location {
  id?: string;
  name: string;
  address: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  geofence_lat?: number | null;
  geofence_lng?: number | null;
  place_id?: string | null;
}

interface LocationFormData {
  name: string;
}

interface LocationWizardProps {
  companyId: string;
  companyData: CompanyData;
  onComplete: () => void;
  onBack: () => void;
}

type LocationView = 'mode' | 'list' | 'details' | 'sports' | 'amenities';

interface LocationStepInfo {
  id: 'details' | 'sports' | 'amenities';
  title: string;
  icon: React.ReactNode;
}

const locationSteps: LocationStepInfo[] = [
  { id: 'details', title: 'Location Details', icon: <Building2 size={18} /> },
  { id: 'sports', title: 'Sports', icon: <Dumbbell size={18} /> },
  { id: 'amenities', title: 'Amenities', icon: <Sparkles size={18} /> },
];

export function LocationWizard({
  companyId,
  companyData,
  onComplete,
  onBack,
}: LocationWizardProps) {
  // List view state
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Wizard state
  const [locationMode, setLocationMode] = useState<'single' | 'multiple' | null>(null);
  const [currentView, setCurrentView] = useState<LocationView>('mode');
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [editingLocationId, setEditingLocationId] = useState<string | null>(null);
  const [isNewLocation, setIsNewLocation] = useState(false);

  // Form state
  const [formData, setFormData] = useState<LocationFormData>({ name: '' });
  const [addressData, setAddressData] = useState<AddressData | null>(null);
  const [saving, setSaving] = useState(false);

  // Sports and amenities selections
  const [selectedSportIds, setSelectedSportIds] = useState<string[]>([]);
  const [selectedAmenityIds, setSelectedAmenityIds] = useState<string[]>([]);

  const loadLocations = useCallback(async () => {
    if (!companyId) return;

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('locations')
        .select('*')
        .eq('company_id', companyId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      if (data && data.length > 0) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        setLocations(data.map((loc: any) => ({
          id: loc.id,
          name: loc.name || '',
          address: loc.address || '',
          address2: loc.address2 || '',
          city: loc.city || '',
          state: loc.state || '',
          zip: loc.zip || '',
          geofence_lat: loc.geofence_lat,
          geofence_lng: loc.geofence_lng,
          place_id: loc.place_id,
        })));
      } else {
        setLocations([]);
      }
    } catch (err: unknown) {
      // Error loading locations
      setError('Failed to load locations');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    if (companyId) {
      loadLocations();
    }
  }, [companyId, loadLocations]);

  // Start adding a new location
  const handleAddLocation = () => {
    const newLocationName = locations.length === 0 ? (companyData.name || 'Main Location') : '';
    setFormData({ name: newLocationName });
    setAddressData(null);
    setEditingLocationId(null);
    setIsNewLocation(true);
    setSelectedSportIds([]);
    setSelectedAmenityIds([]);
    setCurrentStepIndex(0);
    setCurrentView('details');
  };

  // Start editing an existing location
  const handleEditLocation = async (location: Location) => {
    if (!location.id) return;
    
    setLoading(true);
    try {
      // Load full location data including geofence fields
      const { data: fullLocation, error } = await supabase
        .from('locations')
        .select('*')
        .eq('id', location.id)
        .single();

      if (error) throw error;

      if (fullLocation) {
        // Convert to form data format
        setFormData({
          name: fullLocation.name || '',
        });

        // Convert to AddressForm format
        const addressInitialData: Partial<AddressData> = {
          street: fullLocation.address || '',
          unit: fullLocation.address2 || '',
          city: fullLocation.city || '',
          state: fullLocation.state || '',
          zip: fullLocation.zip || '',
          latitude: fullLocation.geofence_lat || undefined,
          longitude: fullLocation.geofence_lng || undefined,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          place_id: (fullLocation as any).place_id || undefined,
        };
        setAddressData(addressInitialData as AddressData);

        if (location.id) {
          setEditingLocationId(location.id);
        }
        setIsNewLocation(false);
        setCurrentStepIndex(0);
        setCurrentView('details');
      }
    } catch (err: unknown) {
      // Error loading location
      setError('Failed to load location data');
    } finally {
      setLoading(false);
    }
  };

  // Delete a location
  const handleDeleteLocation = async (location: Location) => {
    if (locations.length === 1) {
      setError('You must have at least one location');
      return;
    }

    if (location.id) {
      setSaving(true);
      try {
        const { error } = await supabase
          .from('locations')
          .delete()
          .eq('id', location.id);

        if (error) throw error;
        await loadLocations();
      } catch (err: unknown) {
        // Error deleting location
        setError('Failed to delete location');
      } finally {
        setSaving(false);
      }
    }
  };

  // Save location details and move to sports step
  const handleSaveLocationDetails = async (data: { name: string; addressData: AddressData; timezone: string }) => {
    setSaving(true);
    setError(null);

    try {
      if (editingLocationId) {
        // Update existing location
        const { error } = await supabase
          .from('locations')
          .update({
            name: data.name,
            address: data.addressData.street || '',
            address2: data.addressData.unit || '',
            city: data.addressData.city || '',
            state: data.addressData.state || '',
            zip: data.addressData.zip || '',
            geofence_lat: data.addressData.latitude,
            geofence_lng: data.addressData.longitude,
            place_id: data.addressData.place_id,
            timezone: data.timezone,
          })
          .eq('id', editingLocationId);

        if (error) throw error;
      } else {
        // Create new location
        const { data: newLocation, error } = await supabase
          .from('locations')
          .insert({
            company_id: companyId,
            name: data.name,
            address: data.addressData.street || '',
            address2: data.addressData.unit || '',
            city: data.addressData.city || '',
            state: data.addressData.state || '',
            zip: data.addressData.zip || '',
            geofence_lat: data.addressData.latitude,
            geofence_lng: data.addressData.longitude,
            place_id: data.addressData.place_id,
            timezone: data.timezone,
          })
          .select('id')
          .single();

        if (error) throw error;
        setEditingLocationId(newLocation.id);
      }

      // Move to sports step
      setCurrentStepIndex(1);
      setCurrentView('sports');
    } catch (err: unknown) {
      // Error saving location
      setError(err instanceof Error ? err.message : 'Failed to save location');
    } finally {
      setSaving(false);
    }
  };

  // Save sports and move to amenities step
  const handleSaveSports = async () => {
    if (!editingLocationId) return;

    setSaving(true);
    setError(null);

    try {
      const { error } = await supabase.rpc('update_location_sports', {
        location_id_input: editingLocationId,
        sport_ids_input: selectedSportIds,
      });

      if (error) throw error;

      // Move to amenities step
      setCurrentStepIndex(2);
      setCurrentView('amenities');
    } catch (err: unknown) {
      // Error saving sports
      setError(err instanceof Error ? err.message : 'Failed to save sports');
    } finally {
      setSaving(false);
    }
  };

  // Save amenities and complete location setup
  const handleSaveAmenities = async () => {
    if (!editingLocationId) return;

    setSaving(true);
    setError(null);

    try {
      const { error } = await supabase.rpc('update_location_amenities', {
        location_id_input: editingLocationId,
        amenity_ids_input: selectedAmenityIds,
      });

      if (error) throw error;

      // Reload locations
      await loadLocations();

      // Query current location count to determine next step
      const { data: locationData } = await supabase
        .from('locations')
        .select('id')
        .eq('company_id', companyId);

      const locationCount = locationData?.length || 0;

      // After saving amenities
      if (locationMode === 'single' && locationCount === 1) {
        // Single location mode - auto-proceed to billing
        onComplete();
      } else {
        // Multiple location mode or adding to existing - show list view
        setCurrentView('list');
        setEditingLocationId(null);
        setFormData({ name: '' });
        setAddressData(null);
        setIsNewLocation(false);
      }
    } catch (err: unknown) {
      // Error saving amenities
      setError(err instanceof Error ? err.message : 'Failed to save amenities');
    } finally {
      setSaving(false);
    }
  };

  // Go back to previous step
  const handleBack = () => {
    if (currentView === 'list') {
      // List view back goes to company section
      onBack();
    } else if (currentStepIndex === 0) {
      // First step of form wizard
      if (locationMode === 'single' && locations.length === 0) {
        // Go back to mode selection
        setCurrentView('mode');
        setLocationMode(null);
      } else {
        // Go back to list view
        setCurrentView('list');
      }
    } else {
      // Previous step within form wizard
      setCurrentStepIndex(currentStepIndex - 1);
      setCurrentView(locationSteps[currentStepIndex - 1].id);
    }
  };

  // Continue to billing
  const handleContinueToBilling = () => {
    if (locations.length === 0) {
      setError('Please add at least one location');
      return;
    }
    onComplete();
  };

  if (loading && currentView === 'mode') {
    return (
      <div className="min-h-screen bg-[#111010] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-rollops-accent" />
      </div>
    );
  }

  // Mode selection view
  if (currentView === 'mode') {
    return (
      <div className="w-full max-w-2xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-full bg-[#9B1421]/20 flex items-center justify-center mx-auto mb-4">
            <MapPin size={32} className="text-rollops-accent" />
          </div>
          <h1 className="text-2xl font-bold text-[#EBFFEE]">How many locations do you have?</h1>
          <p className="text-[#A9A3A3] mt-2">
            Choose whether you&apos;re setting up a single location or multiple locations
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <button
            onClick={() => {
              setLocationMode('single');
              handleAddLocation();
            }}
            className="bg-[#221F1F] rounded-xl p-6 border-2 border-[#504A4A] hover:border-[#9B1421] transition-colors text-left"
          >
            <div className="w-12 h-12 rounded-full bg-[#9B1421]/20 flex items-center justify-center mb-3">
              <Building2 size={24} className="text-rollops-accent" />
            </div>
            <h3 className="text-lg font-semibold text-[#EBFFEE] mb-2">Single Location</h3>
            <p className="text-sm text-[#A9A3A3]">
              I have one gym location
            </p>
          </button>

          <button
            onClick={() => {
              setLocationMode('multiple');
              setCurrentView('list');
            }}
            className="bg-[#221F1F] rounded-xl p-6 border-2 border-[#504A4A] hover:border-[#9B1421] transition-colors text-left"
          >
            <div className="w-12 h-12 rounded-full bg-[#9B1421]/20 flex items-center justify-center mb-3">
              <MapPin size={24} className="text-rollops-accent" />
            </div>
            <h3 className="text-lg font-semibold text-[#EBFFEE] mb-2">Multiple Locations</h3>
            <p className="text-sm text-[#A9A3A3]">
              I have multiple gym locations
            </p>
          </button>
        </div>

        <div className="flex gap-4">
          <button
            onClick={onBack}
            className="flex-1 py-3 px-4 bg-[#504A4A] hover:bg-[#5A5454] text-white font-semibold rounded-lg transition-colors"
          >
            Back
          </button>
        </div>
      </div>
    );
  }

  // List view
  if (currentView === 'list') {
    return (
      <div className="w-full max-w-2xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-full bg-[#9B1421]/20 flex items-center justify-center mx-auto mb-4">
            <MapPin size={32} className="text-rollops-accent" />
          </div>
          <h1 className="text-2xl font-bold text-[#EBFFEE]">Gym Locations</h1>
          <p className="text-[#A9A3A3] mt-2">
            Add your gym location(s) with sports and amenities
          </p>
        </div>

        {error && (
          <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-3 mb-6">
            <p className="text-red-400 text-sm text-center">{error}</p>
          </div>
        )}

        {/* Locations List */}
        <div className="space-y-4 mb-6">
          {locations.map((location) => (
            <div
              key={location.id}
              className="bg-[#221F1F] rounded-xl p-4 border border-[#504A4A]"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#9B1421]/20 flex items-center justify-center flex-shrink-0">
                    <Building2 size={20} className="text-rollops-accent" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-[#EBFFEE]">{location.name}</h3>
                    </div>
                    <p className="text-sm text-[#A9A3A3] mt-1">
                      {location.address}
                      {location.address2 && `, ${location.address2}`}
                    </p>
                    <p className="text-sm text-[#A9A3A3]">
                      {location.city}, {location.state} {location.zip}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleEditLocation(location)}
                    className="p-2 text-[#A9A3A3] hover:text-[#EBFFEE] hover:bg-[#504A4A] rounded-lg transition-colors"
                    title="Edit location"
                  >
                    <Edit2 size={16} />
                  </button>
                  {locations.length > 1 && (
                    <button
                      onClick={() => handleDeleteLocation(location)}
                      className="p-2 text-[#A9A3A3] hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-colors"
                      title="Delete location"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}

          {/* Add Location Button */}
          <button
            onClick={handleAddLocation}
            className="w-full py-4 border-2 border-dashed border-[#504A4A] rounded-xl text-[#A9A3A3] hover:border-[#9B1421] hover:text-rollops-accent transition-colors flex items-center justify-center gap-2"
          >
            <Plus size={20} />
            {locations.length === 0 ? 'Add Your First Location' : 'Add Another Location'}
          </button>
        </div>

        {/* Navigation Buttons */}
        <div className="flex gap-4">
          <button
            onClick={onBack}
            disabled={saving}
            className="flex-1 py-3 px-4 bg-[#504A4A] hover:bg-[#5A5454] disabled:opacity-50 text-white font-semibold rounded-lg transition-colors"
          >
            Back
          </button>
          <button
            onClick={handleContinueToBilling}
            disabled={saving || locations.length === 0}
            className="flex-1 py-3 px-4 bg-[#9B1421] hover:bg-[#7A0F1A] disabled:bg-[#504A4A] disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            Continue to Billing
            <ChevronRight size={18} />
          </button>
        </div>
      </div>
    );
  }

  // Form wizard view (details → sports → amenities)
  return (
    <div className="w-full max-w-2xl mx-auto px-4 py-8">
      {/* Step Progress - Only show in form wizard */}
      <div className="flex items-center justify-center gap-2 mb-8">
        {locationSteps.map((step, index) => (
          <React.Fragment key={step.id}>
            <div
              className={`
                flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium
                ${index === currentStepIndex
                  ? 'bg-[#9B1421] text-white'
                  : index < currentStepIndex
                    ? 'bg-green-600/20 text-green-500'
                    : 'bg-[#504A4A] text-[#A9A3A3]'
                }
              `}
            >
              {index < currentStepIndex ? (
                <Check size={16} />
              ) : (
                step.icon
              )}
              <span className="hidden sm:inline">{step.title}</span>
            </div>
            {index < locationSteps.length - 1 && (
              <ChevronRight size={16} className="text-[#504A4A]" />
            )}
          </React.Fragment>
        ))}
      </div>

      {error && (
        <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-3 mb-6">
          <p className="text-red-400 text-sm text-center">{error}</p>
        </div>
      )}

      {/* Location Details Step */}
      {currentView === 'details' && (
        <LocationDetailsStep
          initialData={formData}
          addressInitialData={addressData || undefined}
          companyData={companyData}
          onNext={handleSaveLocationDetails}
          onBack={handleBack}
          disabled={saving}
          isNewLocation={isNewLocation}
        />
      )}

      {/* Sports Step */}
      {currentView === 'sports' && companyId && editingLocationId && (
        <div className="bg-[#221F1F] rounded-xl p-6 border border-[#504A4A]">
          <SportsStep
            companyId={companyId}
            locationId={editingLocationId}
            selectedSportIds={selectedSportIds}
            onSelectionChange={setSelectedSportIds}
          />

          {/* Navigation */}
          <div className="flex gap-3 mt-6">
            <button
              onClick={handleBack}
              disabled={saving}
              className="flex-1 py-3 px-4 bg-[#504A4A] hover:bg-[#5A5454] disabled:opacity-50 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              <ChevronLeft size={18} />
              Back
            </button>
            <button
              onClick={handleSaveSports}
              disabled={saving}
              className="flex-1 py-3 px-4 bg-[#9B1421] hover:bg-[#7A0F1A] disabled:bg-[#504A4A] disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              {saving ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  Next: Amenities
                  <ChevronRight size={18} />
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Amenities Step */}
      {currentView === 'amenities' && companyId && editingLocationId && (
        <div className="bg-[#221F1F] rounded-xl p-6 border border-[#504A4A]">
          <AmenitiesStep
            companyId={companyId}
            locationId={editingLocationId}
            selectedAmenityIds={selectedAmenityIds}
            onSelectionChange={setSelectedAmenityIds}
          />

          {/* Navigation */}
          <div className="flex gap-3 mt-6">
            <button
              onClick={handleBack}
              disabled={saving}
              className="flex-1 py-3 px-4 bg-[#504A4A] hover:bg-[#5A5454] disabled:opacity-50 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              <ChevronLeft size={18} />
              Back
            </button>
            <button
              onClick={handleSaveAmenities}
              disabled={saving}
              className="flex-1 py-3 px-4 bg-[#9B1421] hover:bg-[#7A0F1A] disabled:bg-[#504A4A] disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              {saving ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check size={18} />
                  Complete Location
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
